/**
 * Merkle proof helpers.
 *
 * Vendored from the `blockchain-library-merkle_proof` package (v0.0.3) to remove
 * a private nexus-hosted dependency. Implementation kept verbatim.
 *
 * MIT License — Copyright (c) 2022 Animoca Brands.
 */

/**
 * Compute the sibling (companion) node indices needed to build a Merkle proof
 * for the leaf at `leafIndex` in a tree with `leafCount` leaves.
 *
 * @param {number} leafIndex - index of the leaf to prove
 * @param {number} leafCount - total number of leaves in the tree
 * @returns {{ position: 'left' | 'right', index: number, layer: number }[]}
 */
export function getProofIndices(leafIndex, leafCount) {
  const indexes = [];
  const numberOfLayers = Math.log2(leafCount);
  for (let i = 0; i < numberOfLayers; i++) {
    const layerLevelDenominator = Math.pow(2, i);
    const layerSize = Math.ceil(leafCount / layerLevelDenominator);
    const index = Math.floor(leafIndex / layerLevelDenominator);
    const isRight = index % 2;
    const companionIndex = isRight ? index - 1 : index + 1;
    if (companionIndex < layerSize) {
      indexes.push({ position: isRight ? 'left' : 'right', index: companionIndex, layer: i });
    }
  }
  return indexes;
}

/**
 * Build a Merkle proof (array of hex-encoded sibling hashes) for the leaf at
 * `leafIdx`, given the precomputed `layers` of the tree.
 *
 * @param {number} leafIdx - index of the leaf to prove
 * @param {number} leafCount - total number of leaves in the tree
 * @param {Buffer[][]} layers - tree layers, layers[layer][index] is a node hash
 * @returns {string[]} hex-encoded ("0x"-prefixed) sibling hashes
 */
export function getProof(leafIdx, leafCount, layers) {
  const proofIndexes = getProofIndices(leafIdx, leafCount);
  return proofIndexes.map(({ index, layer }) => `0x${layers[layer][index].toString('hex')}`);
}
